import { useSyncExternalStore } from "react";
import { DataTable, IconButton, RotateCwIcon } from "@carbon6/c6-frontend-design";
import { FilterResultCount } from "@/shared/ui/filter";
import { ListToolbar } from "@/shared/ui/ListToolbar";
import { candidateColumns, candidateInitialSort } from "../candidateColumns";
import type { CandidateTableSource } from "../CandidateTableSource";
import styles from "./DatasetListBody.module.css";
import { ListPagination } from "./ListPagination";

type CandidateListBodyProps = {
  source: CandidateTableSource;
  /** 조회 조건이 걸려 있는지 — 빈 목록 문구가 갈린다. */
  filtered: boolean;
  onRefresh: () => void;
};

const LOADING_MESSAGE = "발견된 후보를 불러오고 있습니다.";

const EMPTY_MESSAGE = "아직 제안된 후보가 없습니다. 탐색이 코퍼스를 훑으면 여기에 쌓입니다.";

const EMPTY_FILTER_MESSAGE = "조건에 맞는 후보가 없습니다. 필터를 초기화하고 다시 찾아 주세요.";

/**
 * Scanned Results 페이지의 본문.
 *
 * 카드 보기를 두지 않는다 — 후보는 훑어보는 목록이 아니라 **처리할 대기열**이고, 주소와 포맷을
 * 나란히 비교하는 표가 그 일에 맞다.
 */
export function CandidateListBody({ onRefresh, filtered, source }: CandidateListBodyProps) {
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const loading = snapshot.loadState === "loading";

  return (
    <div className={styles.body}>
      <ListToolbar
        actions={
          <IconButton
            disabled={loading}
            size="sm"
            text={loading ? "불러오는 중" : "새로고침"}
            variant="surface"
            onClick={onRefresh}
          >
            <RotateCwIcon />
          </IconButton>
        }
        ariaLabel="후보 목록 도구"
        summary={<FilterResultCount count={snapshot.total} unitLabel="개" />}
      />
      {snapshot.errorMessage === null ? null : (
        <p className={styles.error} role="alert">
          {snapshot.errorMessage}
        </p>
      )}
      <DataTable
        appearance="framed"
        ariaLabel="발견된 후보 목록"
        columns={candidateColumns}
        emptyMessage={filtered ? EMPTY_FILTER_MESSAGE : EMPTY_MESSAGE}
        initialSort={candidateInitialSort}
        loadingMessage={LOADING_MESSAGE}
        minWidth="900px"
        scrollable
        source={source}
      />
      <ListPagination source={source} />
    </div>
  );
}
